import type { Color } from "../../common/color.js";
import { Control, type ControlOptions } from "../core/Control.js";
import type { Point, Rect, Size } from "../../common/geometry.js";
import type { FontSpec } from "../render/GlyphAtlas.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export type TextAlign = "left" | "center" | "right";
export type VerticalAlign = "top" | "middle" | "bottom";

export interface LabelOptions extends ControlOptions {
  text?: string;
  /** null (or omitted) uses the theme's font. */
  font?: FontSpec | null;
  /** null (or omitted) uses the theme's text color. */
  color?: Color | null;
  textAlign?: TextAlign;
  verticalAlign?: VerticalAlign;
  /** Shrink-wrap the control to the text on every change. */
  autoSize?: boolean;
  /** Truncate with an ellipsis instead of overflowing. */
  ellipsis?: boolean;
  /** Break the text across lines to fit the control's width. */
  wrap?: boolean;
  /** Baseline-to-baseline distance. Defaults to the font's natural spacing. */
  lineHeight?: number | null;
}

/**
 * A static run of text, on one line or wrapped across several.
 *
 * `wrap` is off by default: a label's height is whatever it was given, and
 * silently reflowing existing layouts when this arrived would move things
 * about. Turn it on and the text breaks to the control's width; with
 * `autoSize` as well, the control grows downward to fit.
 */
export class Label extends Control {
  #text: string;
  #font: FontSpec | null;
  #wrapCache: {
    text: string;
    width: number;
    family: string;
    size: number;
    weight: FontSpec["weight"];
    italic: FontSpec["italic"];
    lines: readonly string[];
  } | null = null;
  #autoSize: boolean;
  #wrap: boolean;

  color: Color | null;
  textAlign: TextAlign;
  verticalAlign: VerticalAlign;
  ellipsis: boolean;
  lineHeight: number | null;

  constructor(options: LabelOptions = {}) {
    super({ height: 20, ...options });
    this.#text = options.text ?? "";
    this.#font = options.font ?? null;
    this.#autoSize = options.autoSize ?? false;
    this.#wrap = options.wrap ?? false;
    this.color = options.color ?? null;
    this.textAlign = options.textAlign ?? "left";
    this.verticalAlign = options.verticalAlign ?? "middle";
    this.ellipsis = options.ellipsis ?? true;
    this.lineHeight = options.lineHeight ?? null;
  }

  get wrap(): boolean {
    return this.#wrap;
  }
  set wrap(v: boolean) {
    if (this.#wrap === v) return;
    this.#wrap = v;
    if (this.#autoSize) this.#applyAutoSize();
    this.invalidateMeasure();
    this.invalidate();
  }

  /** The text as it will be drawn, already broken into lines. */
  lines(rc: RenderContext, font: FontSpec): string[] {
    return this.#wrap ? [...this.#wrapped(rc, font, this.width)] : [this.#text];
  }

  /**
   * The text wrapped to `width`, kept until something it depends on changes.
   *
   * A wrapping label used to re-wrap on every paint, and a paint is every
   * frame while anything on the desktop animates. A spec is a plain object an
   * app may edit in place, so its fields are compared rather than its identity.
   */
  #wrapped(rc: RenderContext, font: FontSpec, width: number): readonly string[] {
    const c = this.#wrapCache;
    if (
      c &&
      c.text === this.#text &&
      c.width === width &&
      c.family === font.family &&
      c.size === font.size &&
      c.weight === font.weight &&
      c.italic === font.italic
    ) {
      return c.lines;
    }
    const lines = rc.wrapText(this.#text, font, width);
    this.#wrapCache = {
      text: this.#text,
      width,
      family: font.family,
      size: font.size,
      weight: font.weight,
      italic: font.italic,
      lines,
    };
    return lines;
  }

  get text(): string {
    return this.#text;
  }
  set text(v: string) {
    if (this.#text === v) return;
    this.#text = v;
    this.emit("textchanged");
    if (this.#autoSize) this.#applyAutoSize();
    this.invalidateMeasure();
    this.invalidate();
  }

  get font(): FontSpec | null {
    return this.#font;
  }
  set font(v: FontSpec | null) {
    this.#font = v;
    if (this.#autoSize) this.#applyAutoSize();
    this.invalidateMeasure();
    this.invalidate();
  }

  get autoSize(): boolean {
    return this.#autoSize;
  }
  set autoSize(v: boolean) {
    this.#autoSize = v;
    if (v) this.#applyAutoSize();
  }

  /** Measure and resize to fit. Needs an attached desktop for the font metrics. */
  #applyAutoSize(): void {
    const desktop = this.desktop;
    if (!desktop) return;
    const font = this.#font ?? desktop.theme.metrics.font;
    if (this.#wrap) {
      // Wrapping already fixes the width, so only the height is in question.
      const step = this.lineHeight ?? desktop.rc.lineHeight(font);
      const lines = this.#wrapped(desktop.rc, font, this.width);
      this.setSize(this.width, Math.max(step, lines.length * step));
      return;
    }
    const m = desktop.rc.measureText(this.#text, font);
    this.setSize(Math.ceil(m.width), Math.max(this.height, Math.ceil(m.height)));
  }

  protected override onResize(): void {
    // A narrower label wraps differently, which changes how tall it needs to be.
    if (this.#autoSize && this.#wrap) this.#applyAutoSize();
  }

  protected override onEnvironmentChanged(): void {
    if (this.#autoSize) this.#applyAutoSize();
    this.invalidateMeasure();
  }

  /**
   * As wide as the text, and as tall as the lines it breaks into.
   *
   * The wrapping case is the one that needs `available`: the width is the question already
   * answered, and the height is what follows from it. `autoSize` does the same arithmetic to the
   * label's own bounds; this hands the answer to a parent instead, which is what a layout
   * written without sizes needs.
   */
  override preferredSize(available: Size): Size {
    const desktop = this.desktop;
    if (!desktop) return this.size;
    const font = this.#font ?? desktop.theme.metrics.font;
    const step = this.lineHeight ?? desktop.rc.lineHeight(font);
    if (this.#wrap) {
      const lines = this.#wrapped(desktop.rc, font, available.width);
      return { width: available.width, height: Math.max(step, lines.length * step) };
    }
    const m = desktop.rc.measureText(this.#text, font);
    return { width: Math.ceil(m.width), height: Math.max(step, Math.ceil(m.height)) };
  }

  /**
   * Labels are click-through: the container behind them takes the hit.
   *
   * The point stays in the signature so a subclass that does want the pointer
   * — `LinkLabel` — can narrow the hit to where its text actually is.
   */
  protected override hitTestSelf(_p: Point): boolean {
    return false;
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    if (!this.#text) return;
    const font = this.#font ?? theme.metrics.font;
    const color = this.color ?? (this.effectivelyEnabled ? theme.palette.text : theme.palette.textDisabled);
    const r = this.localRect;
    if (!this.#wrap) {
      drawAlignedText(rc, this.#text, r, font, color, this.textAlign, this.verticalAlign, this.ellipsis);
      return;
    }
    const step = this.lineHeight ?? rc.lineHeight(font);
    rc.drawTextBlock(this.#wrapped(rc, font, r.width), r, font, color, {
      align: this.textAlign,
      verticalAlign: this.verticalAlign,
      lineHeight: step,
      // Only cap the block when the caller asked for an ellipsis; otherwise
      // let it overflow and be clipped by whatever contains it.
      maxLines: this.ellipsis ? Math.max(1, Math.floor(r.height / step)) : 0,
    });
  }
}

/** Shared by Label, Button and header cells. */
export function drawAlignedText(
  rc: RenderContext,
  text: string,
  r: Rect,
  font: FontSpec,
  color: Color,
  align: TextAlign,
  vertical: VerticalAlign,
  ellipsis: boolean,
): void {
  const str = ellipsis ? rc.ellipsize(text, font, r.width) : text;
  if (!str) return;
  // Only a centered or right-aligned run needs its width; this is called for
  // every cell of every visible grid row, so the left-aligned majority skips it.
  let x = r.x;
  if (align === "center") x = r.x + (r.width - rc.measureWidth(str, font)) / 2;
  else if (align === "right") x = r.x + r.width - rc.measureWidth(str, font);

  let y = r.y;
  let baseline: "top" | "middle" | "bottom" = "top";
  if (vertical === "middle") {
    y = r.y + r.height / 2;
    baseline = "middle";
  } else if (vertical === "bottom") {
    y = r.y + r.height;
    baseline = "bottom";
  }
  rc.drawText(str, x, y, font, color, { baseline });
}
